/**
 * Forecast Accuracy KPI (5A)
 * Shows: monthly accuracy trend chart, accuracy metrics, SKU-level results for selected month
 * Filters: Date range, Brand, Store — each with "All" option
 * Export to Excel
 */
import React, { useState, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  TableSortLabel, Divider, TablePagination,
} from '@mui/material';
import {
  Refresh, TrendingUp, TrendingDown,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';

type SortField = 'sku' | 'product_name' | 'brand' | 'category' | 'accuracy' | 'mape' | 'wmape' | 'bias' | 'data_points' | 'total_actual' | 'total_forecast';
type SortDirection = 'asc' | 'desc';

const ForecastAccuracy: React.FC = () => {
  const [periodStart, setPeriodStart] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 12)).toISOString().split('T')[0]
  );
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [monthlyData, setMonthlyData] = useState<any>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sort state for SKU table
  const [sortField, setSortField] = useState<SortField>('accuracy');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state for SKU table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: brands } = useQuery({
    queryKey: ['filter-brands'],
    queryFn: async () => {
      const response = await api.getBrands();
      return response.data;
    },
  });

  const { data: stores } = useQuery({
    queryKey: ['filter-stores'],
    queryFn: async () => {
      const response = await api.getStores();
      return response.data?.results || response.data || [];
    },
  });

  const loadMonthlyAccuracy = async () => {
    setLoading(true);
    setError(null);
    setSelectedMonth(null);
    try {
      const response = await api.getForecastAccuracyMonthly(
        periodStart,
        periodEnd,
        selectedStore || undefined,
        selectedBrand || undefined,
      );
      setMonthlyData(response.data);
      // Auto-select the most recent month with data
      if (response.data?.months?.length > 0) {
        const lastMonth = response.data.months[response.data.months.length - 1];
        setSelectedMonth(lastMonth.month);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load monthly accuracy data');
    } finally {
      setLoading(false);
    }
  };

  // Chart data for monthly accuracy trend
  const chartData = useMemo(() => {
    if (!monthlyData?.months) return [];
    return monthlyData.months.map((m: any) => ({
      month: m.month_label,
      monthKey: m.month,
      accuracy: m.overall_metrics.accuracy,
      mape: m.overall_metrics.mape,
      wmape: m.overall_metrics.wmape,
      bias: m.overall_metrics.bias,
      products: m.total_products,
    }));
  }, [monthlyData]);

  // Selected month data
  const selectedMonthData = useMemo(() => {
    if (!monthlyData?.months || !selectedMonth) return null;
    return monthlyData.months.find((m: any) => m.month === selectedMonth);
  }, [monthlyData, selectedMonth]);

  // Sorted SKU list for selected month
  const sortedSkuList = useMemo(() => {
    if (!selectedMonthData?.all_products) return [];
    const list = [...selectedMonthData.all_products];
    list.sort((a: any, b: any) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [selectedMonthData, sortField, sortDirection]);

  // Paginated SKU list
  const paginatedSkuList = useMemo(() => {
    return sortedSkuList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedSkuList, page, rowsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'accuracy' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const handleChartClick = (data: any) => {
    if (data?.activePayload?.[0]?.payload?.monthKey) {
      setSelectedMonth(data.activePayload[0].payload.monthKey);
      setPage(0);
    }
  };

  const exportRows = useMemo(() => {
    if (!selectedMonthData?.all_products) return [];
    return selectedMonthData.all_products.map((p: any) => ({
      SKU: p.sku || '',
      Product: p.product_name,
      Brand: p.brand || '',
      Category: p.category,
      'Actual Sales': Math.round(p.total_actual ?? 0),
      'Qty Forecasted': Math.round(p.total_forecast ?? 0),
      'Accuracy %': p.accuracy?.toFixed(1),
      'MAPE %': p.mape?.toFixed(1),
      'WMAPE %': p.wmape?.toFixed(1),
      Bias: p.bias?.toFixed(1),
      MAE: p.mae?.toFixed(2),
      RMSE: p.rmse?.toFixed(2),
      'Data Points': p.data_points,
    }));
  }, [selectedMonthData]);

  const getAccuracyColor = (value: number) => {
    if (value >= 80) return 'var(--color-success)';
    if (value >= 60) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  const getBarColor = (accuracy: number) => {
    if (accuracy >= 80) return '#16A34A';
    if (accuracy >= 60) return '#F59E0B';
    return '#DC2626';
  };

  return (
    <Box>
      {/* Header & Filters */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Forecast Accuracy
          </Typography>
          {selectedMonthData && (
            <ExportMenu
              data={exportRows}
              filename={`Forecast_Accuracy_${selectedMonth}`}
              title={`Forecast Accuracy — ${selectedMonth}`}
              sheetName="Forecast Accuracy"
              chartRef={chartContainerRef}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mb: 2 }}>
          Monthly forecast accuracy trend. Click on a month bar to view detailed SKU-level results.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth type="date" label="Period Start" value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth type="date" label="Period End" value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select value={selectedBrand} label="Brand" onChange={(e) => setSelectedBrand(e.target.value)}>
                <MenuItem value="">All Brands</MenuItem>
                {brands?.map((b: string) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Store</InputLabel>
              <Select value={selectedStore} label="Store" onChange={(e) => setSelectedStore(e.target.value)}>
                <MenuItem value="">All Stores</MenuItem>
                {(stores as any[])?.map((s: any) => (
                  <MenuItem key={s.store_id} value={s.store_id}>
                    {s.store_name || s.name || s.store_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained" startIcon={<Refresh />} onClick={loadMonthlyAccuracy}
              fullWidth sx={{ height: '56px' }} disabled={loading}
            >
              {loading ? 'Loading...' : 'Load'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {/* KPI Cards — between filters and chart */}
      {selectedMonthData && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Results for: {selectedMonthData.month_label}
              </Typography>
              <Chip
                label={`${selectedMonthData.total_products} SKUs`}
                size="small"
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {monthlyData?.months?.map((m: any) => (
                <Chip
                  key={m.month}
                  label={m.month_label}
                  size="small"
                  variant={selectedMonth === m.month ? 'filled' : 'outlined'}
                  color={selectedMonth === m.month ? 'primary' : 'default'}
                  onClick={() => { setSelectedMonth(m.month); setPage(0); }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  Products
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonthData.total_products}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  Accuracy
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: getAccuracyColor(selectedMonthData.overall_metrics.accuracy), fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonthData.overall_metrics.accuracy.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  MAPE
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonthData.overall_metrics.mape.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  WMAPE
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonthData.overall_metrics.wmape.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  Bias
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonthData.overall_metrics.bias.toFixed(1)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  RMSE
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonthData.overall_metrics.rmse.toFixed(1)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Monthly Accuracy Chart */}
      {chartData.length > 0 && (
        <Paper ref={chartContainerRef} sx={{ p: 3, mb: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
          <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', mb: 2 }}>
            Monthly Forecast Accuracy Trend
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={chartData} onClick={handleChartClick} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e0e0e0)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: 'var(--color-text-muted, #666)' }}
              />
              <YAxis
                yAxisId="accuracy"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: 'var(--color-text-muted, #666)' }}
                label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'var(--color-text-muted, #666)' } }}
              />
              <YAxis
                yAxisId="mape"
                orientation="right"
                tick={{ fontSize: 12, fill: 'var(--color-text-muted, #666)' }}
                label={{ value: 'MAPE %', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: 'var(--color-text-muted, #666)' } }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0]?.payload;
                  return (
                    <Paper sx={{ p: 1.5, border: '1px solid var(--color-border)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
                      <Typography variant="caption" display="block">
                        Accuracy: <strong style={{ color: getBarColor(data.accuracy) }}>{data.accuracy.toFixed(1)}%</strong>
                      </Typography>
                      <Typography variant="caption" display="block">MAPE: {data.mape.toFixed(1)}%</Typography>
                      <Typography variant="caption" display="block">WMAPE: {data.wmape.toFixed(1)}%</Typography>
                      <Typography variant="caption" display="block">Bias: {data.bias.toFixed(1)}</Typography>
                      <Typography variant="caption" display="block">Products: {data.products}</Typography>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">Click to view SKU details</Typography>
                    </Paper>
                  );
                }}
              />
              <Legend />
              <ReferenceLine yAxisId="accuracy" y={80} stroke="#16A34A" strokeDasharray="4 4" label={{ value: '80% target', position: 'insideTopRight', fontSize: 11, fill: '#16A34A' }} />
              <Bar
                yAxisId="accuracy"
                dataKey="accuracy"
                name="Accuracy %"
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              >
                {chartData.map((entry: any, index: number) => (
                  <Cell
                    key={`bar-${index}`}
                    fill={getBarColor(entry.accuracy)}
                    opacity={selectedMonth === entry.monthKey ? 1 : 0.6}
                    stroke={selectedMonth === entry.monthKey ? '#1a1a1a' : 'none'}
                    strokeWidth={selectedMonth === entry.monthKey ? 2 : 0}
                  />
                ))}
              </Bar>
              <Line
                yAxisId="mape"
                type="monotone"
                dataKey="mape"
                name="MAPE %"
                stroke="#7C3AED"
                strokeWidth={2}
                dot={{ r: 4, fill: '#7C3AED' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            Click on a bar to select a month and view SKU-level details below
          </Typography>
        </Paper>
      )}

      {/* SKU-level results table */}
      {selectedMonthData && (
        <>
          {/* SKU-level results table */}
          <Paper sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                All SKU Results — {selectedMonthData.month_label}
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                {sortedSkuList.length} SKUs
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-head': { fontSize: '11px', fontWeight: 700, py: '6px', px: '8px', whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ width: 36 }}>#</TableCell>
                    <TableCell sx={{ width: 90 }}>
                      <TableSortLabel active={sortField === 'sku'} direction={sortField === 'sku' ? sortDirection : 'asc'} onClick={() => handleSort('sku')}>SKU</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <TableSortLabel active={sortField === 'product_name'} direction={sortField === 'product_name' ? sortDirection : 'asc'} onClick={() => handleSort('product_name')}>Product</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 90 }}>
                      <TableSortLabel active={sortField === 'brand'} direction={sortField === 'brand' ? sortDirection : 'asc'} onClick={() => handleSort('brand')}>Brand</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>
                      <TableSortLabel active={sortField === 'category'} direction={sortField === 'category' ? sortDirection : 'asc'} onClick={() => handleSort('category')}>Category</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      <TableSortLabel active={sortField === 'total_actual'} direction={sortField === 'total_actual' ? sortDirection : 'desc'} onClick={() => handleSort('total_actual')}>Actual</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      <TableSortLabel active={sortField === 'total_forecast'} direction={sortField === 'total_forecast' ? sortDirection : 'desc'} onClick={() => handleSort('total_forecast')}>Forecast</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 76 }}>
                      <TableSortLabel active={sortField === 'accuracy'} direction={sortField === 'accuracy' ? sortDirection : 'desc'} onClick={() => handleSort('accuracy')}>Accuracy</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 64 }}>
                      <TableSortLabel active={sortField === 'mape'} direction={sortField === 'mape' ? sortDirection : 'asc'} onClick={() => handleSort('mape')}>MAPE</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 68 }}>
                      <TableSortLabel active={sortField === 'wmape'} direction={sortField === 'wmape' ? sortDirection : 'asc'} onClick={() => handleSort('wmape')}>WMAPE</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 60 }}>
                      <TableSortLabel active={sortField === 'bias'} direction={sortField === 'bias' ? sortDirection : 'asc'} onClick={() => handleSort('bias')}>Bias</TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 50 }}>
                      <TableSortLabel active={sortField === 'data_points'} direction={sortField === 'data_points' ? sortDirection : 'desc'} onClick={() => handleSort('data_points')}>Pts</TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSkuList.length > 0 ? (
                    paginatedSkuList.map((product: any, idx: number) => (
                      <TableRow key={product.product_id} hover sx={{ '& .MuiTableCell-body': { fontSize: '11px', py: '4px', px: '8px' } }}>
                        <TableCell sx={{ color: 'var(--color-text-muted)' }}>
                          {page * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {product.sku || '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {product.product_name}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--color-text-muted)' }}>{product.brand || '-'}</TableCell>
                        <TableCell sx={{ color: 'var(--color-text-muted)' }}>{product.category}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {(product.total_actual ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {(product.total_forecast ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            component="span"
                            sx={{ color: getAccuracyColor(product.accuracy), fontWeight: 600, fontSize: '11px' }}
                          >
                            {product.accuracy.toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{product.mape.toFixed(1)}%</TableCell>
                        <TableCell align="right">{product.wmape.toFixed(1)}%</TableCell>
                        <TableCell align="right">
                          <Typography
                            component="span"
                            sx={{
                              color: product.bias > 0 ? 'var(--color-warning)' : product.bias < 0 ? '#8B2438' : 'inherit',
                              fontSize: '11px',
                            }}
                          >
                            {product.bias > 0 ? '+' : ''}{product.bias.toFixed(1)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{product.data_points}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No forecast accuracy data for this month.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {sortedSkuList.length > 0 && (
              <TablePagination
                component="div"
                count={sortedSkuList.length}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Paper>
        </>
      )}

      {/* Empty state */}
      {!monthlyData && !loading && !error && (
        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '48px 20px', textAlign: 'center', background: 'var(--color-surface)' }}>
          <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-secondary)', mb: 0.5 }}>
            No Data Available
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Select a date range (recommended: last 12 months) and click "Load" to view the monthly forecast accuracy trend.
            Click on any month bar in the chart to see SKU-level details.
          </Typography>
        </Box>
      )}

      {monthlyData && chartData.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No forecast accuracy data available for the selected filters. Make sure forecasts and sales data exist for the selected period.
        </Alert>
      )}
    </Box>
  );
};

export default ForecastAccuracy;
